import React from "react";
import { Stack } from "@mui/material";

import RadioOptionCard from "./RadioOptionCard";

export interface RadioCardOption<T extends string> {
  value: T;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}

interface RadioCardGroupProps<T extends string> {
  ariaLabel: string;
  value: T | "";
  options: Array<RadioCardOption<T>>;
  onChange: (value: T) => void;
  /** When set, renders the cards in a horizontal row instead of stacked vertically. */
  direction?: "row" | "column";
  /** Optional renderer for inline expanded content under the selected card. */
  renderExpanded?: (value: T) => React.ReactNode;
  /** Optional renderer for the top-right slot of each card. */
  renderRightSlot?: (value: T, selected: boolean) => React.ReactNode;
}

/**
 * Renders a list of selectable `RadioOptionCard`s.
 * Generic on the value type for type-safe option arrays.
 */
function RadioCardGroup<T extends string>({
  ariaLabel,
  value,
  options,
  onChange,
  direction = "column",
  renderExpanded,
  renderRightSlot,
}: RadioCardGroupProps<T>) {
  return (
    <Stack
      role="radiogroup"
      aria-label={ariaLabel}
      direction={direction}
      gap={4}
      sx={direction === "row" ? { flexWrap: "wrap", "& > *": { flex: "1 1 200px" } } : undefined}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <RadioOptionCard
            key={opt.value}
            value={opt.value}
            label={opt.label}
            description={opt.description}
            selected={selected}
            disabled={opt.disabled}
            onSelect={(v) => onChange(v as T)}
            expandedContent={renderExpanded && selected ? renderExpanded(opt.value) : undefined}
            rightSlot={renderRightSlot ? renderRightSlot(opt.value, selected) : undefined}
          />
        );
      })}
    </Stack>
  );
}

export default RadioCardGroup;
